import React, { useState, useRef, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

// ─── i18n ─────────────────────────────────────────────────────────────────────
const T = {
  en: {
    editProfile:      'Edit Profile',
    firstName:        'First Name',
    paternalSurname:  'Paternal Surname',
    maternalSurname:  'Maternal Surname',
    email:            'Email',
    cancel:           'Cancel',
    save:             'Save Changes',
    saving:           'Saving…',
    nameTooShort:     'Name must be at least 2 characters',
    updateSuccess:    'Profile updated successfully',
    updateError:      'Error updating: ',
    editProfileMenu:  'Edit Profile',
    logout:           'Sign Out',
    loggingOut:       'Signing out…',
    defaultUser:      'User',
  },
  es: {
    editProfile:      'Editar perfil',
    firstName:        'Nombre',
    paternalSurname:  'Apellido Paterno',
    maternalSurname:  'Apellido Materno',
    email:            'Email',
    cancel:           'Cancelar',
    save:             'Guardar cambios',
    saving:           'Guardando…',
    nameTooShort:     'El nombre debe tener al menos 2 caracteres',
    updateSuccess:    'Perfil actualizado correctamente',
    updateError:      'Error al actualizar: ',
    editProfileMenu:  'Editar Perfil',
    logout:           'Cerrar Sesión',
    loggingOut:       'Cerrando…',
    defaultUser:      'Usuario',
  },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (name) => {
  if (!name?.trim()) return 'U';
  const parts = name.trim().split(' ').filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// ─── Avatar ───────────────────────────────────────────────────────────────────
const Avatar = ({ photoURL, name, size = 40, fontSize = 15 }) => {
  const [imgError, setImgError] = useState(false);
  const initials = getInitials(name);

  useEffect(() => { setImgError(false); }, [photoURL]);

  const base = {
    width: size, height: size,
    borderRadius: '50%', overflow: 'hidden', flexShrink: 0,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  };

  if (photoURL && !imgError) {
    return (
      <div style={base}>
        <img
          src={photoURL}
          alt={name}
          referrerPolicy="no-referrer"
          onError={() => setImgError(true)}
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
      </div>
    );
  }

  return (
    <div style={{
      ...base,
      background: 'linear-gradient(135deg, #6366f1, #7c3aed)',
      color: '#fff', fontSize, fontWeight: 700,
      fontFamily: '"DM Sans", sans-serif', letterSpacing: '0.02em',
    }}>
      {initials}
    </div>
  );
};

// ─── Field ────────────────────────────────────────────────────────────────────
const Field = ({ label, value, onChange, disabled = false, type = 'text', autoFocus = false }) => (
  <div>
    <label style={{
      display: 'block', marginBottom: 6, fontSize: 13,
      fontWeight: 600, color: '#374151', fontFamily: '"DM Sans", sans-serif',
    }}>
      {label}
    </label>
    <input
      type={type}
      value={value}
      onChange={onChange}
      disabled={disabled}
      autoFocus={autoFocus}
      style={{
        width: '100%', padding: '9px 12px',
        border: '1.5px solid #e5e7eb', borderRadius: 8,
        fontSize: 14, fontFamily: '"DM Sans", sans-serif',
        boxSizing: 'border-box',
        background: disabled ? '#f9fafb' : '#fff',
        color: disabled ? '#9ca3af' : '#111827',
        cursor: disabled ? 'not-allowed' : 'text',
        outline: 'none', transition: 'border-color 0.15s',
      }}
      onFocus={(e) => { if (!disabled) e.target.style.borderColor = '#6366f1'; }}
      onBlur={(e)  => { e.target.style.borderColor = '#e5e7eb'; }}
    />
  </div>
);

// ─── EditUserModal ────────────────────────────────────────────────────────────
const EditUserModal = ({ isOpen, onClose, user, userData, onUpdate, t }) => {
  const [nom,     setNom]     = useState('');
  const [epellP,  setEpellP]  = useState('');
  const [epellM,  setEpellM]  = useState('');
  const [loading, setLoading] = useState(false);
  const [toast,   setToast]   = useState(null);

  useEffect(() => {
    if (isOpen && userData) {
      setNom(userData?.nom?.nom       || '');
      setEpellP(userData?.nom?.epellP || '');
      setEpellM(userData?.nom?.epellM || '');
      setToast(null);
    }
  }, [isOpen, userData]);

  const showToast = (type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSave = async () => {
    if (nom.trim().length < 2) { showToast('error', t.nameTooShort); return; }
    setLoading(true);
    try {
      await updateDoc(doc(db, 'users', user.uid), {
        nom: { nom: nom.trim(), epellP: epellP.trim(), epellM: epellM.trim() },
      });
      showToast('success', t.updateSuccess);
      onUpdate?.();
      setTimeout(onClose, 1200);
    } catch (err) {
      showToast('error', t.updateError + err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const fullName = [nom, epellP, epellM].filter(Boolean).join(' ') || t.defaultUser;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 9999, backdropFilter: 'blur(4px)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: '#fff', borderRadius: 16, width: '92%', maxWidth: 460,
          boxShadow: '0 24px 48px rgba(0,0,0,0.18)', overflow: 'hidden',
          fontFamily: '"DM Sans", sans-serif',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{
          padding: '20px 24px', borderBottom: '1px solid #f3f4f6',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <Avatar photoURL={user?.photoURL} name={fullName} size={42} fontSize={15} />
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#111827' }}>{t.editProfile}</div>
              <div style={{ fontSize: 12, color: '#9ca3af' }}>{user?.email}</div>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: '#9ca3af', fontSize: 22, lineHeight: 1,
              padding: 4, borderRadius: 6, display: 'flex', alignItems: 'center',
            }}
          >✕</button>
        </div>

        {/* Toast */}
        {toast && (
          <div style={{
            margin: '12px 24px 0', padding: '10px 14px', borderRadius: 8,
            fontSize: 13, fontWeight: 500,
            background: toast.type === 'success' ? '#f0fdf4' : '#fef2f2',
            color:      toast.type === 'success' ? '#15803d'  : '#dc2626',
            border:    `1px solid ${toast.type === 'success' ? '#bbf7d0' : '#fecaca'}`,
          }}>
            {toast.type === 'success' ? '✓ ' : '⚠ '}{toast.msg}
          </div>
        )}

        {/* Body */}
        <div style={{ padding: '20px 24px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <Field label={t.firstName}       value={nom}    onChange={(e) => setNom(e.target.value)}    autoFocus />
          <Field label={t.paternalSurname} value={epellP} onChange={(e) => setEpellP(e.target.value)} />
          <Field label={t.maternalSurname} value={epellM} onChange={(e) => setEpellM(e.target.value)} />
          <Field label={t.email}           value={user?.email || ''} disabled type="email" />
        </div>

        {/* Footer */}
        <div style={{
          padding: '16px 24px', borderTop: '1px solid #f3f4f6',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            onClick={onClose}
            disabled={loading}
            style={{
              padding: '9px 18px', borderRadius: 8, border: '1.5px solid #e5e7eb',
              fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer',
              background: '#fff', color: '#374151',
              fontFamily: '"DM Sans", sans-serif', opacity: loading ? 0.5 : 1,
            }}
          >
            {t.cancel}
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            style={{
              padding: '9px 20px', borderRadius: 8, border: 'none',
              fontSize: 14, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer',
              background: 'linear-gradient(135deg,#6366f1,#7c3aed)', color: '#fff',
              fontFamily: '"DM Sans", sans-serif', opacity: loading ? 0.7 : 1,
              boxShadow: '0 4px 12px rgba(99,102,241,0.3)',
            }}
          >
            {loading ? t.saving : t.save}
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── MenuBtn ──────────────────────────────────────────────────────────────────
const MenuBtn = ({ icon, label, onClick, disabled = false, danger = false }) => {
  const [hover, setHover] = useState(false);
  return (
    <button
      onClick={!disabled ? onClick : undefined}
      disabled={disabled}
      style={{
        width: '100%', padding: '11px 16px',
        background: hover ? '#f9fafb' : 'transparent',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'flex', alignItems: 'center', gap: 10,
        fontSize: 14, fontWeight: 500,
        color: danger ? '#ef4444' : '#374151',
        fontFamily: '"DM Sans", sans-serif',
        opacity: disabled ? 0.55 : 1, transition: 'background 0.15s',
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
    >
      <span style={{ fontSize: 17 }}>{icon}</span>
      <span>{label}</span>
    </button>
  );
};

// ─── UserProfile ──────────────────────────────────────────────────────────────
const UserProfile = ({ currentUser, userData, onLogout }) => {
  const { language } = useAuth();
  const t = language === 'es' ? T.es : T.en;

  const [showMenu,      setShowMenu]      = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const menuRef = useRef(null);

  const fullName = (() => {
    if (currentUser?.displayName) return currentUser.displayName;
    if (userData?.nom?.nom) {
      return [userData.nom.nom, userData.nom.epellP, userData.nom.epellM]
        .filter(Boolean).join(' ');
    }
    return t.defaultUser;
  })();

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowMenu(false);
    };
    if (showMenu) document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showMenu]);

  const handleLogout = async () => {
    setLogoutLoading(true);
    try { await onLogout?.(); }
    catch (err) { console.error(err); }
    finally { setLogoutLoading(false); }
  };

  return (
    <>
      <div style={{ position: 'relative' }} ref={menuRef}>

        {/* Trigger */}
        <button
          onClick={() => setShowMenu((v) => !v)}
          style={{
            width: 40, height: 40, borderRadius: '50%', padding: 0,
            border: showMenu ? '2px solid #6366f1' : '2px solid #e5e7eb',
            background: 'none', cursor: 'pointer', overflow: 'hidden',
            transition: 'border-color 0.2s, transform 0.15s',
            transform: showMenu ? 'scale(1.05)' : 'scale(1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = '#6366f1';
            e.currentTarget.style.transform   = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            if (!showMenu) {
              e.currentTarget.style.borderColor = '#e5e7eb';
              e.currentTarget.style.transform   = 'scale(1)';
            }
          }}
        >
          <Avatar photoURL={currentUser?.photoURL} name={fullName} size={36} fontSize={14} />
        </button>

        {/* Dropdown */}
        {showMenu && (
          <div style={{
            position: 'absolute', top: 'calc(100% + 10px)', right: 0,
            background: '#fff', border: '1.5px solid #e5e7eb',
            borderRadius: 12, boxShadow: '0 8px 28px rgba(0,0,0,0.12)',
            minWidth: 240, zIndex: 1000, overflow: 'hidden',
            animation: 'fadeDown 0.15s ease',
            fontFamily: '"DM Sans", sans-serif',
          }}>

            {/* User info */}
            <div style={{
              padding: '14px 16px', borderBottom: '1px solid #f3f4f6',
              display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <Avatar photoURL={currentUser?.photoURL} name={fullName} size={44} fontSize={17} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{
                  fontSize: 14, fontWeight: 700, color: '#111827',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {fullName}
                </div>
                <div style={{
                  fontSize: 12, color: '#9ca3af',
                  whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                }}>
                  {currentUser?.email}
                </div>
              </div>
            </div>

            <MenuBtn
              icon="✏️"
              label={t.editProfileMenu}
              onClick={() => { setShowMenu(false); setShowEditModal(true); }}
            />
            <MenuBtn
              icon="🚪"
              label={logoutLoading ? t.loggingOut : t.logout}
              onClick={handleLogout}
              disabled={logoutLoading}
              danger
            />
          </div>
        )}
      </div>

      <EditUserModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={currentUser}
        userData={userData}
        onUpdate={() => console.log('Profile updated — Context will refresh via onSnapshot')}
        t={t}
      />

      <style>{`
        @keyframes fadeDown {
          from { opacity: 0; transform: translateY(-6px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default UserProfile;